import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface Props {
  open: boolean
  onClose: () => void
}

/**
 * Failed Login Modal
 * Shows a message when login fails, with 10-second auto-dismiss countdown
 */
export default function FailedLoginModal({ open, onClose }: Props) {
  const [countdown, setCountdown] = useState(10)

  useEffect(() => {
    if (!open) return

    // Reset countdown when modal opens
    setCountdown(10)

    // Countdown timer
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          onClose()
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4 hover-raise"
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4 text-gray-900">Login Unsuccessful</h2>
            <p className="text-gray-700 mb-4">
              Note: If you are a parent and finding your login doesn't work, note that your login will not be activated until your registration has been approved by a Parish Admin User. Please be patient!
            </p>
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Closing automatically in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
              </div>
              <button
                onClick={onClose}
                className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
              >
                Close Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

